import { defineStore } from 'pinia'
import { RequestService } from '../services/RequestService'

const { handleErrors } = RequestService()

// A lightweight persistence helper
function persistSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    handleErrors('Persisting value', error)
  }
}
function persistGet(key, fallback) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch (error) {
    handleErrors('Persisting value', error)
    return fallback
  }
}

export const useReportsStore = defineStore('reports', {
  state: () => ({
    employer: persistGet('reports_employer', {
      name: '',
      ein: '',
      planYear: '',
      planYearId: '',
      groupInsuranceType: '',
    }),
    // Derived from plan questions most used by PDFs
    planDerived: persistGet('reports_planDerived', {
      controlledGroup: '',
      erisaPlan: '',
      newHire: '',
      medicalPlan: '',
      fullTimeEmployers: '',
      coverages: [],
      isPlanComplete: false,
    }),
    // Planning Tool specific
    planning: persistGet('reports_planning', {
      selectedTabKey: '',
      selectionsByTab: {}, // { [tabKey]: Array<{ discussionPoint, notes, responsibleParty }> }
      headerBgSize: 'small',
      title: '',
      whitelabelColor: '',
      printWithCustomLogo: false,
    }),
    // Calendar specific cache
    calendar: persistGet('reports_calendar', {
      templateID: '',
      events: [],
      filteredEvents: [],
      recipients: [],
      yearOfActualPlan: '',
      calendarType: '',
    }),
    // Gap report specific cache
    gapReport: persistGet('reports_gapReport', {
      sections: [], // mapped itemsTab
      overallScore: 0,
      totalCompRisk: 0,
    }),
  }),

  actions: {
    // Canonical employer/plan info
    setEmployerInfo({ name, ein, planYear, planYearId, groupInsuranceType }) {
      this.employer = {
        name: name ?? this.employer.name,
        ein: ein ?? this.employer.ein,
        planYear: planYear ?? this.employer.planYear,
        planYearId: planYearId ?? this.employer.planYearId,
        groupInsuranceType: groupInsuranceType ?? this.employer.groupInsuranceType,
      }
      persistSet('reports_employer', this.employer)
    },

    setPlanDerived(payload) {
      this.planDerived = { ...this.planDerived, ...payload }
      persistSet('reports_planDerived', this.planDerived)
    },

    // Planning Tool
    setPlanningTab(tabKey) {
      this.planning.selectedTabKey = tabKey
      persistSet('reports_planning', this.planning)
    },
    setPlanningSelections(tabKey, items) {
      this.planning.selectionsByTab = {
        ...this.planning.selectionsByTab,
        [tabKey]: items || [],
      }
      persistSet('reports_planning', this.planning)
    },
    setPlanningPresentation({ headerBgSize, title, whitelabelColor, printWithCustomLogo }) {
      this.planning.headerBgSize = headerBgSize ?? this.planning.headerBgSize
      this.planning.title = title ?? this.planning.title
      this.planning.whitelabelColor = whitelabelColor ?? this.planning.whitelabelColor
      this.planning.printWithCustomLogo = !!printWithCustomLogo
      persistSet('reports_planning', this.planning)
    },

    // Calendar
    setCalendarTemplateID(id) {
      this.calendar.templateID = id
      persistSet('reports_calendar', this.calendar)
    },
    setCalendarContext({ events, filteredEvents, recipients, yearOfActualPlan, calendarType }) {
      this.calendar.events = events ?? this.calendar.events
      this.calendar.filteredEvents = filteredEvents ?? this.calendar.filteredEvents
      this.calendar.recipients = recipients ?? this.calendar.recipients
      this.calendar.yearOfActualPlan = yearOfActualPlan ?? this.calendar.yearOfActualPlan
      this.calendar.calendarType = calendarType ?? this.calendar.calendarType
      persistSet('reports_calendar', this.calendar)
    },

    // Gap report
    setGapSections({ sections, overallScore, totalCompRisk }) {
      if (sections) this.gapReport.sections = sections
      if (typeof overallScore === 'number') this.gapReport.overallScore = overallScore
      if (typeof totalCompRisk === 'number') this.gapReport.totalCompRisk = totalCompRisk
      persistSet('reports_gapReport', this.gapReport)
    },
  },

  getters: {
    // Agency disclaimers replace the default footer context in Carbone. When
    // no agency disclaimer exists, keep agencyInfo so the default template
    // can resolve the agency name and use DEFAULT_REPORT_FOOTER as the text.
    reportFooterData: () => ({ agencyInfo = {}, footerDisclaimer = '', defaultFooterDisclaimer = '' } = {}) => {
      const agencyFooter = typeof footerDisclaimer === 'string' ? footerDisclaimer.trim() : ''
      if (agencyFooter) {
        return { agencyInfo, footerDisclaimer: agencyFooter, dfooterDisclaimer: '' }
      }
      return {
        agencyInfo,
        footerDisclaimer: '',
        dfooterDisclaimer: typeof defaultFooterDisclaimer === 'string' ? defaultFooterDisclaimer.trim() : ''
      }
    },
    planningPdfData: (state) => (tabKey) => {
      const key = tabKey || state.planning.selectedTabKey
      return {
        agencyInfo: {}, 
        employer: state.employer.name,
        title: state.planning.title,
        medicalPlan: state.planDerived.medicalPlan,
        ein: state.employer.ein,
        controlledGroup: state.planDerived.controlledGroup,
        erisaPlan: state.planDerived.erisaPlan,
        newHire: state.planDerived.newHire,
        coverages: state.planDerived.coverages,
        fullTimeEmployers: state.planDerived.fullTimeEmployers,
        planYear: state.employer.planYear,
        customLogo: null,
        logoSize: '',
        footerDisclaimer: '',
        printWithCustomLogo: state.planning.printWithCustomLogo,
        whitelabelColor: state.planning.whitelabelColor,
        isPlanComplete: state.planDerived.isPlanComplete,
        headerBgSize: state.planning.headerBgSize,
        selectedTabPage: {
          page: '1',
          mainTitle: key,
          questionsSelectedTab: state.planning.selectionsByTab[key] || []
        }
      }
    },
    // Build Calendar PDF payload by merging cache + external page content
    calendarPdfData: (state) => ({
      agencyInfo = {},
      title = '',
      evPageCal = [], // result of formatEventsForPdf(months)
      ongoingRequirementsBody = '',
      printWithCustomLogo = false,
      whitelabelColor = '',
      customLogo = null,
      logoSize = '',
      footerDisclaimer = '',
      dfooterDisclaimer = '',
      isPlanComplete = state.planDerived.isPlanComplete,
    } = {}) => {
      return {
        agencyInfo,
        employer: state.employer.name,
        title,
        medicalPlan: state.planDerived.medicalPlan,
        ein: state.employer.ein,
        controlledGroup: state.planDerived.controlledGroup,
        erisaPlan: state.planDerived.erisaPlan,
        newHire: state.planDerived.newHire,
        coverages: state.planDerived.coverages,
        fullTimeEmployers: state.planDerived.fullTimeEmployers,
        planYear: state.employer.planYear,
        customLogo,
        logoSize,
        footerDisclaimer,
        dfooterDisclaimer,
        printWithCustomLogo,
        whitelabelColor,
        isPlanComplete,
        coverPage: {},
        evPage: {
          page: '1',
          description: '', 
          cal: evPageCal,
        },
        ongoingRequirementsPage: {
          page: '2',
          mainTitle: 'Ongoing Requirements',
          ongoingRequirements: ongoingRequirementsBody,
        }
      }
    },
    // Build Gap Report PDF payload by merging cache + external page content
    gapPdfData: (state) => ({
      agencyInfo = {},
      summaryPage = null,
      actionItemsPage = null,
      tabPages = [],
      printWithCustomLogo = false,
      whitelabelColor = '',
      customLogo = null,
      logoSize = '',
      footerDisclaimer = '',
      dfooterDisclaimer = '',
      isPlanComplete = state.planDerived.isPlanComplete,
      tableOfContents = []
    } = {}) => {
      return {
        agencyInfo,
        employer: state.employer.name,
        medicalPlan: state.planDerived.medicalPlan,
        ein: state.employer.ein,
        controlledGroup: state.planDerived.controlledGroup,
        erisaPlan: state.planDerived.erisaPlan,
        newHire: state.planDerived.newHire,
        coverages: state.planDerived.coverages,
        fullTimeEmployers: state.planDerived.fullTimeEmployers,
        planYear: state.employer.planYear,
        customLogo,
        logoSize,
        footerDisclaimer,
        dfooterDisclaimer,
        isPlanComplete,
        printWithCustomLogo,
        whitelabelColor,
        tableOfContents,
        summaryPage: summaryPage || {},
        actionItemsPage: actionItemsPage || {},
        tabPages: tabPages || [],
      }
    },
  }
})
